/**
 * Menu: the one menu-button implementation (ROW-PATTERN-001 step 2, ported
 * from parkify-demo #106 `CMenu`, merge 023b4c6).
 *
 * THE KEYBOARD CONTRACT. The trigger is a real button with aria-haspopup,
 * aria-expanded and (while open) aria-controls. Enter, Space and ArrowDown open
 * the menu with focus on the first item; ArrowUp opens it on the last. Arrows
 * move within the menu and wrap across every item, refused ones included; Home
 * and End jump to the ends. Escape closes it and returns focus to the trigger.
 * Tab closes it and lets focus move on. A keyboard pick closes it and returns
 * focus to the trigger before the item runs.
 *
 * POINTER BEHAVIOUR IS UNCHANGED. A pointer click (event.detail >= 1) toggles
 * the menu and does not move focus; an outside mousedown closes it. Focus
 * moves into the menu only when the keyboard or assistive technology opened it
 * (keydown on the trigger, or a click with detail 0, which is what a screen
 * reader's activate sends).
 *
 * THE MENU IS ITS OWN TARGET. Its root stops click propagation, so a menu that
 * sits inside a row that opens something can never open the row as well.
 *
 * A REFUSED ITEM STAYS FOCUSABLE. It is aria-disabled, never native disabled:
 * the arrows reach it, a keyboard open lands on it even when every item is
 * refused, and activating it does nothing and keeps the menu open. Its reason
 * is its accessible description, so it is read when the item takes focus. With
 * native disabled the reason was a title tooltip on a button that could never
 * take focus, so a keyboard user could not reach it at all.
 */
import {
  useEffect, useId, useLayoutEffect, useRef, useState,
  type CSSProperties, type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject,
} from 'react'

export interface MenuItem {
  key: string
  label: string
  /** Visible second line. Also the item's accessible description. */
  description?: string
  /**
   * When set, the item is refused: aria-disabled, still focusable, inert when
   * activated, and this text is its accessible description (and tooltip).
   */
  disabledReason?: string
  danger?: boolean
  onSelect: () => void
}

export interface MenuTriggerProps {
  ref: RefObject<HTMLButtonElement>
  id: string
  type: 'button'
  'aria-haspopup': 'menu'
  'aria-expanded': boolean
  'aria-controls': string | undefined
  onClick: (e: ReactMouseEvent<HTMLButtonElement>) => void
  onKeyDown: (e: ReactKeyboardEvent<HTMLButtonElement>) => void
}

export interface MenuProps {
  items: MenuItem[]
  /** Render the trigger button and spread these props onto it. */
  renderTrigger: (props: MenuTriggerProps, open: boolean) => ReactNode
  /** Which edge of the trigger the menu hangs from. */
  align?: 'left' | 'right'
  /**
   * `absolute` anchors below the trigger inside a relative wrapper. `fixed`
   * positions from the trigger's rect, for triggers inside a scroll container
   * that would clip an absolute menu; it follows the trigger on scroll and resize.
   */
  placement?: 'absolute' | 'fixed'
  minWidth?: number
  /** Controlled open state. Omit to let the menu own it. */
  open?: boolean
  /** Called whenever the menu asks to open or close. */
  onOpenChange?: (open: boolean) => void
}

type FocusTarget = 'first' | 'last'

const menuStyle: CSSProperties = {
  zIndex: 60, minWidth: 150, padding: 4, textAlign: 'left',
  background: 'var(--color-white, #fff)', borderRadius: 10,
  border: '1px solid var(--color-gray-200, #e5e5ea)',
  boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
}

const itemBase: CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
  fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', background: 'transparent',
  border: 'none', borderRadius: 6, whiteSpace: 'nowrap',
}

export function Menu({
  items, renderTrigger, align = 'right', placement = 'absolute', minWidth = 150,
  open: openProp, onOpenChange,
}: MenuProps) {
  const [innerOpen, setInnerOpen] = useState(false)
  const open = openProp ?? innerOpen
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number } | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const focusOnOpen = useRef<FocusTarget | null>(null)
  const uid = useId()
  const triggerId = `menu-t${uid}`
  const menuId = `menu-m${uid}`

  const setOpen = (next: boolean) => {
    if (openProp === undefined) setInnerOpen(next)
    onOpenChange?.(next)
  }

  const menuItems = () => menuRef.current
    ? Array.from(menuRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]'))
    : []

  const place = () => {
    if (placement !== 'fixed') return
    const r = triggerRef.current?.getBoundingClientRect()
    if (!r) return
    setPos(align === 'right'
      ? { top: r.bottom + 4, right: window.innerWidth - r.right }
      : { top: r.bottom + 4, left: r.left })
  }

  const close = (refocus: boolean) => {
    setOpen(false)
    if (refocus) triggerRef.current?.focus()
  }

  const openWith = (where: FocusTarget | null) => {
    if (open) {
      if (where) {
        const its = menuItems()
        const t = where === 'last' ? its[its.length - 1] : its[0]
        t?.focus()
      }
      return
    }
    focusOnOpen.current = where
    place()
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return
      if (menuRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(true) }
    // A fixed menu follows its trigger rather than closing: focusing a
    // trigger inside a scroll container scrolls it into view, and that scroll
    // can land just after a keyboard open, which would close the menu under
    // the focused item and drop focus to <body>.
    const onMove = () => place()
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    if (placement === 'fixed') {
      // Capture catches an inner table scroll too.
      window.addEventListener('scroll', onMove, true)
      window.addEventListener('resize', onMove)
    }
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
    // close/setOpen are recreated per render but only touch refs and state setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placement])

  useLayoutEffect(() => {
    if (!open || !focusOnOpen.current) return
    const where = focusOnOpen.current
    focusOnOpen.current = null
    const its = menuItems()
    const t = where === 'last' ? its[its.length - 1] : its[0]
    t?.focus()
  }, [open])

  const onTriggerKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (open) close(false); else openWith('first')
    } else if (e.key === 'ArrowDown') {
      e.preventDefault(); openWith('first')
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); openWith('last')
    }
  }

  const onTriggerClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    if (e.detail === 0) {
      if (open) close(false); else openWith('first')
      return
    }
    if (open) setOpen(false); else openWith(null)
  }

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const its = menuItems()
    const at = its.indexOf(document.activeElement as HTMLElement)
    const go = (i: number) => {
      e.preventDefault()
      if (its.length) its[(i + its.length) % its.length].focus()
    }
    if (e.key === 'ArrowDown') go(at + 1)
    else if (e.key === 'ArrowUp') go(at < 0 ? its.length - 1 : at - 1)
    else if (e.key === 'Home') go(0)
    else if (e.key === 'End') go(its.length - 1)
    else if (e.key === 'Tab') setOpen(false)
  }

  const onRootKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && open) {
      e.preventDefault(); e.stopPropagation(); close(true)
    }
  }

  const popupPosition: CSSProperties = placement === 'fixed'
    ? { position: 'fixed', top: pos?.top ?? 0, left: pos?.left, right: pos?.right }
    : { position: 'absolute', top: 'calc(100% + 4px)', [align]: 0 }

  const trigger: MenuTriggerProps = {
    ref: triggerRef, id: triggerId, type: 'button',
    'aria-haspopup': 'menu', 'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
    onClick: onTriggerClick, onKeyDown: onTriggerKeyDown,
  }

  return (
    <div
      ref={rootRef}
      onClick={e => e.stopPropagation()}
      onKeyDown={onRootKeyDown}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      {renderTrigger(trigger, open)}
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-labelledby={triggerId}
          onKeyDown={onMenuKeyDown}
          style={{ ...menuStyle, ...popupPosition, minWidth }}
        >
          {items.map((item, i) => {
            const refused = !!item.disabledReason
            const second = item.disabledReason ?? item.description
            const descId = `${menuId}-d${i}`
            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                tabIndex={-1}
                aria-disabled={refused ? 'true' : undefined}
                aria-describedby={second ? descId : undefined}
                title={item.disabledReason}
                onClick={e => {
                  if (refused) { e.preventDefault(); return }
                  const keyboard = e.detail === 0
                  setOpen(false)
                  if (keyboard) triggerRef.current?.focus()
                  item.onSelect()
                }}
                onMouseEnter={e => {
                  if (!refused) e.currentTarget.style.background = item.danger ? '#fef2f2' : 'var(--color-gray-100, #f2f2f5)'
                }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                style={{
                  ...itemBase,
                  cursor: refused ? 'not-allowed' : 'pointer',
                  color: refused
                    ? 'var(--color-gray-400, #9a95a0)'
                    : (item.danger ? '#b91c1c' : 'var(--color-gray-700, #3a3540)'),
                }}
              >
                {item.label}
                {item.description && !refused ? (
                  <span id={descId} style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--color-gray-500, #6b6b6b)', marginTop: 2, whiteSpace: 'normal' }}>
                    {item.description}
                  </span>
                ) : second ? (
                  // A refused item's reason: its description, read when it takes focus.
                  <span id={descId} hidden>{second}</span>
                ) : null}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
